import React, { ComponentProps } from 'react'
import Stack from '@mui/material/Stack'
import { Tooltip, Typography } from '@mui/material'
import SvgIcon from '@mui/material/SvgIcon'
import { ReactComponent as ReorderModeIcon } from '../../../../assets/images/actions/reorder-mode.svg'
import { ReactComponent as CloseIcon } from '../../../../assets/images/Close.svg'
import { useIsSnapshotMode } from '../../../../shared/hooks/useIsSnapshotMode'
import { useIsRoadmapReadonly } from '../../../../shared/hooks/useIsRoadmapReadonly'

type ValueNarrativeReorderProps = Pick<ComponentProps<typeof SvgIcon>, 'sx'> & {
    isReorderModeActive: boolean
    hasReorderChanges: boolean
    onEnterReorderMode: () => void
    onSaveReorder: () => void | Promise<void>
    onCancelReorder: () => void
}

export const ValueNarrativeReorder = ({
    isReorderModeActive,
    hasReorderChanges,
    onEnterReorderMode,
    onSaveReorder,
    onCancelReorder,
    sx,
}: ValueNarrativeReorderProps) => {
    const isSnapshotMode = useIsSnapshotMode()
    const isReadonlyMode = useIsRoadmapReadonly()

    if (isSnapshotMode || isReadonlyMode) {
        return null
    }

    const handleReorderClick = async () => {
        if (isReorderModeActive) {
            await onSaveReorder()
        } else {
            onEnterReorderMode()
        }
    }

    const showSaveLabel = isReorderModeActive && hasReorderChanges

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={[
                {
                    borderRadius: '999px',
                    bgcolor: isReorderModeActive ? 'figma.primary2' : 'transparent',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <Tooltip title={isReorderModeActive ? 'Save order' : 'Reorder'} arrow>
                <Stack
                    onClick={handleReorderClick}
                    direction="row"
                    px={1}
                    py={0.75}
                    alignItems="center"
                    sx={{
                        color: isReorderModeActive ? 'figma.primary' : 'figma.grayscale.tier2',
                        borderRadius: '999px',
                        cursor: 'pointer',
                        '&:hover': {
                            color: 'figma.primary',
                        },
                    }}
                >
                    <SvgIcon
                        component={ReorderModeIcon}
                        inheritViewBox
                        fontSize="small"
                        color="inherit"
                        sx={{ fontSize: '18px' }}
                    />
                    {showSaveLabel && (
                        <Typography
                            variant="14x400"
                            color="inherit"
                            whiteSpace="nowrap"
                            ml={0.5}
                            sx={{ textTransform: 'uppercase' }}
                        >
                            Save
                        </Typography>
                    )}
                </Stack>
            </Tooltip>
            {isReorderModeActive && (
                <Tooltip title="Cancel" arrow>
                    <Stack
                        onClick={onCancelReorder}
                        direction="row"
                        px={1}
                        py={0.75}
                        alignItems="center"
                        sx={{
                            color: 'figma.grayscale.textHier2',
                            borderRadius: '999px',
                            cursor: 'pointer',
                            '&:hover': {
                                color: 'figma.primary',
                            },
                        }}
                    >
                        <SvgIcon
                            component={CloseIcon}
                            inheritViewBox
                            fontSize="small"
                            color="inherit"
                            sx={{ fontSize: '18px' }}
                        />
                    </Stack>
                </Tooltip>
            )}
        </Stack>
    )
}

export default ValueNarrativeReorder
